import './dsFocus.js';

/**
 * Button — OutCRM's action control. Radius 10, Inter 500.
 *
 * HEIGHT (locked, iron rule 2): every hierarchy sits on --control-height (32),
 * `secondary` on --control-height-compact (28). Named sizes change padding, type and
 * gap only, never the tier. Hierarchies: primary (purple CTA) · secondary (grey fill,
 * 28) · tertiary (ghost) · outline (hairline ring) · link · destructive.
 *
 * Ported from the skill's components/core/Button.jsx, prop names preserved
 * (HANDOFF.md:27). Idiom per spec S4: every visual lives in ds.css, so the reference's
 * hover/active useState pair is replaced by :hover / :active rules — the rendered
 * result is the same, the inline style object AC-9 bans is gone. The focus ring comes
 * from the shared dsFocus stylesheet via data-ds-focusable (spec S8), not from a local
 * focus style (HANDOFF.md:29).
 */
const HIERARCHIES = new Set(['primary', 'secondary', 'tertiary', 'outline', 'link', 'destructive']);
const SIZES = new Set(['small', 'medium', 'large', 'xlarge']);

export function Button ({
  children, hierarchy = 'primary', size = 'medium', iconOnly = false,
  leadingIcon, trailingIcon, disabled = false, loading = false, className, ...rest
}) {
  // `brand` is an alias of primary; icon-only primary/secondary fall back to outline.
  let h = hierarchy === 'brand' ? 'primary' : hierarchy;
  if (!HIERARCHIES.has(h)) h = 'primary';
  if (iconOnly && (h === 'primary' || h === 'secondary')) h = 'outline';
  const s = SIZES.has(size) ? size : 'medium';
  const inert = disabled || loading;

  return (
    <button
      type="button"
      disabled={inert}
      aria-busy={loading || undefined}
      data-ds-focusable
      className={[
        'oc-btn', `oc-btn--${h}`, `oc-btn--${s}`,
        iconOnly && 'oc-btn--icon-only',
        loading && 'oc-btn--loading',
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {loading && <span className="oc-btn__spinner" aria-hidden="true" />}
      <span className="oc-btn__label">
        {leadingIcon}
        {children}
        {trailingIcon}
      </span>
    </button>
  );
}
